import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Send, X, Loader, Upload, Search, FileVideo, CheckCircle, AlertCircle, FolderDown, Image as ImageIcon } from 'lucide-react';
import { useStore } from '../store';
import { motion, AnimatePresence } from 'framer-motion';
import { VideoUploader } from './VideoUploader';
import { supabase } from '../lib/supabase';
import { useArtistClient } from '../contexts/ArtistClientContext';
import JSZip from 'jszip';
import { convertHeicToJpeg, isHeicFile } from '../lib/releaseAssets';

interface SubmissionFormProps {
  onClose: () => void;
  artistId?: string;
}

interface PendingFile {
  id: string;
  file: File;
  progress: number;
  status: 'uploading' | 'done' | 'error';
  url?: string;
  originalFileName?: string;
  error?: string;
  fileSize: string;
  kind: 'video' | 'image';
  previewUrl?: string;
}

// Accepted file types
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];

const isImageFile = (file: File): boolean => {
  const lowerType = (file.type || '').toLowerCase();
  if (IMAGE_TYPES.includes(lowerType)) return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return !!ext && IMAGE_EXTENSIONS.includes(ext);
};

const isVideoFile = (file: File): boolean => {
  if (VIDEO_TYPES.includes(file.type)) return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return !!ext && ['mp4', 'webm', 'mov', 'quicktime'].includes(ext);
};

interface FolderLogEntry {
  message: string;
  type: 'info' | 'success' | 'error' | 'progress';
  timestamp: string;
}

export const SubmissionForm: React.FC<SubmissionFormProps> = ({ onClose, artistId }) => {
  const { addSubmission, artists } = useStore();
  const { client: artistClient } = useArtistClient();
  const db = artistClient || supabase;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [releases, setReleases] = useState<any[]>([]);
  const [selectedRelease, setSelectedRelease] = useState<string>('');
  // Dropbox folder import state
  const [isProcessingFolder, setIsProcessingFolder] = useState(false);
  const [folderLogs, setFolderLogs] = useState<FolderLogEntry[]>([]);

  const [formData, setFormData] = useState<{
    projectName: string;
    videoUrl: string;
    type: 'song-specific' | 'off-topic';
    artistId: string;
    notes: string;
  }>({
    projectName: '',
    videoUrl: '',
    type: 'song-specific',
    artistId: artistId || '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [artistSearch, setArtistSearch] = useState('');
  const [showArtistDropdown, setShowArtistDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Track whether the user manually edited the Video Name field.
  // If not touched, the filename is used as the default submission name.
  const [projectNameTouched, setProjectNameTouched] = useState(false);

  // Filter out archived artists
  const activeArtists = artists.filter(artist => !artist.archived);
  
  // Get selected artist name
  const selectedArtistName = useMemo(() => {
    if (!formData.artistId) return '';
    const artist = activeArtists.find(a => String(a.id) === String(formData.artistId));
    return artist?.name || '';
  }, [formData.artistId, activeArtists]);
  
  // Filter artists based on search
  const filteredArtists = useMemo(() => {
    if (!artistSearch.trim()) return activeArtists;
    const search = artistSearch.toLowerCase();
    return activeArtists.filter(artist => 
      artist.name.toLowerCase().includes(search)
    );
  }, [artistSearch, activeArtists]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowArtistDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Revoke object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      pendingFiles.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch releases for selected artist
  useEffect(() => {
    const fetchReleases = async () => {
      const currentArtistId = artistId || formData.artistId;
      if (!currentArtistId) {
        setReleases([]);
        setSelectedRelease('');
        return;
      }

      try {
        const { data, error } = await db
          .from('releases')
          .select('id, name, release_date, created_at, service_package')
          .eq('artist_id', currentArtistId as any)
          .order('release_date', { ascending: false, nullsFirst: false });

        if (error) throw error;

        const fetchedReleases = (data || []) as any[];
        setReleases(fetchedReleases);

        // Auto-select the latest created release by default.
        // Only fall back to sessionStorage if the saved release is the latest
        // (i.e. user submitted to the newest release last time and nothing newer was added since).
        const savedReleaseKey = `selected_release_submission_${currentArtistId}`;
        const savedRelease = sessionStorage.getItem(savedReleaseKey);

        if (fetchedReleases && fetchedReleases.length > 0) {
          const latestRelease = fetchedReleases[0]; // first due to order by created_at desc
          if (savedRelease && savedRelease === latestRelease.id) {
            setSelectedRelease(savedRelease);
          } else {
            // Always default to the latest created release
            setSelectedRelease(latestRelease.id);
          }
        } else {
          setSelectedRelease('');
        }
      } catch (error) {
        console.error('Error fetching releases:', error);
        setReleases([]);
        setSelectedRelease('');
      }
    };

    fetchReleases();
  }, [artistId, formData.artistId]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || seconds <= 0) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatSpeed = (bytesPerSecond: number): string => {
    if (bytesPerSecond === 0) return '0 KB/s';
    const k = 1024;
    const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
    const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
    return parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const uploadSingleFile = useCallback(async (file: File, fileId: string, kind: 'video' | 'image') => {
    if (kind === 'image') {
      // Validate image type
      if (!isImageFile(file)) {
        setPendingFiles(prev => prev.map(f => f.id === fileId ? {
          ...f, status: 'error', error: 'Invalid image format (JPG, PNG, WebP, GIF only)'
        } : f));
        return;
      }

      try {
        setPendingFiles(prev => prev.map(f => f.id === fileId ? { ...f, progress: 0 } : f));

        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(2, 15);
        const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
        const storagePath = `uploads/${timestamp}_${randomId}.${fileExt}`;

        const { error: uploadError } = await db.storage
          .from('submission-images')
          .upload(storagePath, file, {
            cacheControl: '3600',
            upsert: false,
            contentType: file.type || `image/${fileExt}`,
          });

        if (uploadError) {
          throw new Error(uploadError.message);
        }

        const { data: urlData } = db.storage.from('submission-images').getPublicUrl(storagePath);
        const originalFileName = file.name.replace(/\.[^/.]+$/, '');

        setPendingFiles(prev => prev.map(f => f.id === fileId ? {
          ...f, status: 'done', url: urlData.publicUrl, originalFileName, progress: 100
        } : f));
      } catch (err) {
        setPendingFiles(prev => prev.map(f => f.id === fileId ? {
          ...f, status: 'error', error: err instanceof Error ? err.message : 'Upload failed'
        } : f));
      }
      return;
    }

    // Video upload (existing flow)
    if (!isVideoFile(file)) {
      setPendingFiles(prev => prev.map(f => f.id === fileId ? {
        ...f, status: 'error', error: 'Invalid format (MP4, WebM, MOV only)'
      } : f));
      return;
    }

    try {
      const uploader = new VideoUploader({
        onStart: () => {
          setPendingFiles(prev => prev.map(f => f.id === fileId ? { ...f, progress: 0 } : f));
        },
        onProgress: (progress) => {
          setPendingFiles(prev => prev.map(f => f.id === fileId ? { ...f, progress } : f));
        },
        onUploadComplete: (url, originalFileName) => {
          setPendingFiles(prev => prev.map(f => f.id === fileId ? {
            ...f, status: 'done', url, originalFileName, progress: 100
          } : f));
        },
        onError: (uploadError) => {
          setPendingFiles(prev => prev.map(f => f.id === fileId ? {
            ...f, status: 'error', error: uploadError
          } : f));
        }
      });

      await uploader.uploadFile(file);
    } catch (err) {
      setPendingFiles(prev => prev.map(f => f.id === fileId ? {
        ...f, status: 'error', error: err instanceof Error ? err.message : 'Upload failed'
      } : f));
    }
  }, [db]);

  const addFiles = useCallback(async (fileList: FileList | File[]) => {
    const files = Array.from(fileList).filter(f => isVideoFile(f) || isImageFile(f));

    if (files.length === 0) {
      setError('Please select valid video (MP4, WebM, MOV) or image (JPG, PNG, WebP, GIF) files.');
      return;
    }

    setError(null);
    setIsUploading(true);

    // Convert HEIC/HEIF files to JPEG so all browsers can preview & display them.
    // Non-HEIC files pass through unchanged. If conversion fails, we still
    // include the original file (upload may still work, just no browser preview).
    const hasHeic = files.some(isHeicFile);
    if (hasHeic) {
      console.log(`[HEIC] Detected ${files.filter(isHeicFile).length} HEIC file(s), converting...`);
      setIsConverting(true);
    }
    const convertedFiles = await Promise.all(
      files.map(async f => {
        try {
          return await convertHeicToJpeg(f);
        } catch (err) {
          console.error(`[HEIC] Conversion failed for ${f.name}:`, err);
          return f; // fall back to original
        }
      })
    );
    if (hasHeic) setIsConverting(false);

    const newPending: PendingFile[] = convertedFiles.map(file => ({
      id: `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      file,
      progress: 0,
      status: 'uploading' as const,
      fileSize: formatFileSize(file.size),
      kind: isImageFile(file) ? 'image' as const : 'video' as const,
      previewUrl: isImageFile(file) ? URL.createObjectURL(file) : undefined,
    }));

    setPendingFiles(prev => [...prev, ...newPending]);

    // Auto-fill Video Name with the first file's name if the user hasn't manually edited it.
    // For images (single or carousel): prefix with "photo_slide_".
    // For a single video: use the filename directly.
    // For multiple videos: leave empty (each video gets its own name on submit).
    if (!projectNameTouched && files.length >= 1) {
      const firstFileName = files[0].name.replace(/\.[^/.]+$/, '');
      const imageCount = newPending.filter(f => f.kind === 'image').length;
      const videoCount = newPending.filter(f => f.kind === 'video').length;
      if (imageCount > 0 && videoCount === 0) {
        // Images (single or carousel) → prefix with photo_slide_
        setFormData(prev => ({ ...prev, projectName: `photo_slide_${firstFileName}` }));
      } else if (files.length === 1) {
        setFormData(prev => ({ ...prev, projectName: firstFileName }));
      } else {
        setFormData(prev => ({ ...prev, projectName: '' }));
      }
    }

    // Upload all files in parallel
    await Promise.all(
      newPending.map(pf => uploadSingleFile(pf.file, pf.id, pf.kind))
    );

    setIsUploading(false);

    // Clear file input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadSingleFile, projectNameTouched]);

  const removePendingFile = useCallback((fileId: string) => {
    setPendingFiles(prev => {
      const removed = prev.find(f => f.id === fileId);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      const filtered = prev.filter(f => f.id !== fileId);
      // If the name wasn't manually edited, update the auto-filled name
      const imageCount = filtered.filter(f => f.kind === 'image').length;
      const videoCount = filtered.filter(f => f.kind === 'video').length;
      if (!projectNameTouched) {
        if (imageCount > 0 && videoCount === 0) {
          // Images (single or carousel) → prefix with photo_slide_
          const firstImage = filtered.find(f => f.kind === 'image');
          if (firstImage) {
            const name = (firstImage.originalFileName || firstImage.file.name.replace(/\.[^/.]+$/, ''));
            setFormData(prev => ({ ...prev, projectName: `photo_slide_${name}` }));
          }
        } else if (filtered.length === 1 && filtered[0].status === 'done') {
          const name = (filtered[0].originalFileName || filtered[0].file.name.replace(/\.[^/.]+$/, ''));
          setFormData(prev => ({ ...prev, projectName: name }));
        } else if (filtered.length !== 1) {
          setFormData(prev => ({ ...prev, projectName: '' }));
        }
      }
      return filtered;
    });
  }, [projectNameTouched]);

  // Dropbox folder import — uses the edge function as a CORS proxy to download
  // the ZIP, then extracts + uploads + creates submissions client-side.
  // The edge function just passes the Dropbox ZIP through (no memory/timeout issues).
  const FEEDBACKTOOL_SUPABASE_URL = 'https://wrlgoxbzlngdtomjhvnz.supabase.co';

  const isDropboxFolderUrl = (url: string): boolean => {
    return url.includes('dropbox.com/scl/fo/') || url.includes('dropbox.com/sh/');
  };

  const addFolderLog = (message: string, type: FolderLogEntry['type'] = 'info') => {
    setFolderLogs(prev => [...prev, { message, type, timestamp: new Date().toISOString() }]);
  };

  const processDropboxFolder = async () => {
    const url = formData.videoUrl.trim();
    if (!url) {
      setError('Please paste a Dropbox folder URL');
      return;
    }
    if (!isDropboxFolderUrl(url)) {
      setError('Please provide a valid Dropbox folder URL (contains /scl/fo/ or /sh/)');
      return;
    }
    const currentArtistId = artistId || formData.artistId;
    if (!currentArtistId) {
      setError('Please select an artist first');
      return;
    }

    setIsProcessingFolder(true);
    setFolderLogs([]);
    setError(null);

    let hadError = false;

    try {
      // Get the current user's session token for edge function auth
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || '';

      addFolderLog('Downloading ZIP via proxy...', 'progress');

      // Use edge function as CORS proxy — it just streams the Dropbox ZIP back
      const response = await fetch(`${FEEDBACKTOOL_SUPABASE_URL}/functions/v1/process-dropbox-folder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ dropbox_folder_url: url }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(`Proxy returned ${response.status}: ${errText}`);
      }

      const contentLength = response.headers.get('content-length');
      const totalSize = contentLength ? parseInt(contentLength, 10) : null;
      if (totalSize) {
        addFolderLog(`File size: ${(totalSize / 1024 / 1024).toFixed(1)} MB`, 'info');
      }

      // Read the ZIP data with progress tracking
      const reader = response.body?.getReader();
      if (!reader) throw new Error('Failed to read response stream');

      const chunks: Uint8Array[] = [];
      let downloadedBytes = 0;
      const startTime = Date.now();
      let lastProgressUpdate = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        downloadedBytes += value.length;

        const now = Date.now();
        if (now - lastProgressUpdate > 500) {
          const downloadedMB = downloadedBytes / 1024 / 1024;
          const elapsedSec = (now - startTime) / 1000;
          const speedMBps = elapsedSec > 0 ? downloadedMB / elapsedSec : 0;
          if (totalSize) {
            const pct = Math.round((downloadedBytes / totalSize) * 100);
            const remainingMB = (totalSize - downloadedBytes) / 1024 / 1024;
            const etaSec = speedMBps > 0 ? remainingMB / speedMBps : 0;
            addFolderLog(`Downloading: ${downloadedMB.toFixed(1)} MB / ${(totalSize / 1024 / 1024).toFixed(1)} MB (${pct}%) — ${speedMBps.toFixed(1)} MB/s — ETA: ${Math.round(etaSec)}s`, 'progress');
          } else {
            addFolderLog(`Downloading: ${downloadedMB.toFixed(1)} MB — ${speedMBps.toFixed(1)} MB/s`, 'progress');
          }
          lastProgressUpdate = now;
        }
      }

      const totalMB = (downloadedBytes / 1024 / 1024).toFixed(1);
      const elapsedSec = ((Date.now() - startTime) / 1000).toFixed(1);
      addFolderLog(`Downloaded ${totalMB} MB in ${elapsedSec}s`, 'success');

      // Combine chunks
      const totalLength = chunks.reduce((acc, c) => acc + c.length, 0);
      const zipData = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        zipData.set(chunk, offset);
        offset += chunk.length;
      }
      chunks.length = 0;

      // Extract ZIP client-side
      addFolderLog('Extracting ZIP...', 'progress');
      const zip = await JSZip.loadAsync(zipData);

      const videoExtensions = ['.mp4', '.mov', '.webm', '.avi', '.mkv', '.m4v'];
      const videoEntries = Object.entries(zip.files).filter(([filename, file]) => {
        if ((file as any).dir) return false;
        const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
        return videoExtensions.includes(ext);
      });

      if (videoEntries.length === 0) {
        addFolderLog('No video files found in the ZIP', 'error');
        hadError = true;
        return;
      }

      addFolderLog(`Found ${videoEntries.length} video files`, 'success');

      // Upload each video to Storage + create submission
      let created = 0;
      for (let i = 0; i < videoEntries.length; i++) {
        const [filename, file] = videoEntries[i];
        const progress = `[${i + 1}/${videoEntries.length}]`;
        const fileName = filename.split('/').pop() || filename;
        const cleanName = fileName.replace(/\.[^/.]+$/, '');

        try {
          addFolderLog(`${progress} Extracting: ${fileName}`, 'progress');
          const videoData = await (file as any).async('uint8array');
          const fileSizeMB = (videoData.length / 1024 / 1024).toFixed(1);

          addFolderLog(`${progress} Uploading: ${fileName} (${fileSizeMB} MB)`, 'progress');

          const timestamp = Date.now();
          const randomId = Math.random().toString(36).substring(2, 15);
          const ext = fileName.substring(fileName.lastIndexOf('.'));
          const storagePath = `uploads/${timestamp}_${randomId}${ext}`;

          const { error: uploadError } = await db.storage
            .from('videos')
            .upload(storagePath, videoData, {
              contentType: `video/${ext.replace('.', '')}`,
              upsert: false,
            });

          if (uploadError) {
            addFolderLog(`${progress} Upload failed: ${uploadError.message}`, 'error');
            continue;
          }

          const { data: urlData } = db.storage.from('videos').getPublicUrl(storagePath);

          await addSubmission({
            projectName: cleanName,
            videoUrl: urlData.publicUrl,
            type: formData.type,
            artistId: currentArtistId,
            notes: formData.notes,
            status: 'new',
            release_id: selectedRelease || null,
          } as any, artistClient || undefined);

          created++;
          addFolderLog(`${progress} Created submission: ${cleanName}`, 'success');
        } catch (err) {
          addFolderLog(`${progress} Failed: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
        }
      }

      addFolderLog(`Done! Created ${created} of ${videoEntries.length} submissions`, 'success');

      if (!hadError && created > 0) {
        setFormData(prev => ({ ...prev, videoUrl: '' }));
        setTimeout(() => onClose(), 1500);
      }
    } catch (err) {
      console.error('Error processing Dropbox folder:', err);
      addFolderLog(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
      hadError = true;
    } finally {
      setIsProcessingFolder(false);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      await addFiles(e.target.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await addFiles(e.dataTransfer.files);
    }
  };

  const completedFiles = pendingFiles.filter(f => f.status === 'done');
  const hasUploadingFiles = pendingFiles.some(f => f.status === 'uploading');
  const hasErrorFiles = pendingFiles.some(f => f.status === 'error');

  // Disable uploads when the selected release is ad_only (no Content-Feedback package)
  const selectedReleaseObj = releases.find(r => r.id === selectedRelease);
  const isAdOnlyRelease = selectedRelease && selectedReleaseObj?.service_package === 'ad_only';

  const validateForm = () => {
    // Video name is optional when files are uploaded (filename used as fallback)
    if (!formData.projectName.trim() && completedFiles.length === 0) {
      setError('Please enter a project name');
      return false;
    }

    // Check for Instagram reel URLs
    if (formData.videoUrl.includes('instagram.com') && 
        (formData.videoUrl.includes('/reel/') || formData.videoUrl.includes('/p/'))) {
      setError('Instagram URLs should be submitted in the Ad Creatives section, not here.');
      return false;
    }
    
    // Check for TikTok auth codes
    if (formData.videoUrl.trim().startsWith('#')) {
      setError('TikTok auth codes should be submitted in the Ad Creatives section, not here.');
      return false;
    }

    // Need either uploaded files or a URL
    if (completedFiles.length === 0 && !formData.videoUrl.trim()) {
      setError('Please upload at least one video/image or provide a video URL');
      return false;
    }

    // Check for Instagram reel URLs or TikTok auth codes in notes
    if (formData.notes) {
      if (formData.notes.includes('instagram.com') && 
          (formData.notes.includes('/reel/') || formData.notes.includes('/p/'))) {
        setError('Instagram URLs should be submitted in the Ad Creatives section, not here.');
        return false;
      }
      
      if (formData.notes.trim().startsWith('#')) {
        setError('TikTok auth codes should be submitted in the Ad Creatives section, not here.');
        return false;
      }
    }

    if (!formData.artistId) {
      setError('Please select an artist');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loading || isUploading || hasUploadingFiles) return;
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Save selected release to session storage
      const currentArtistId = artistId || formData.artistId;
      if (selectedRelease && currentArtistId) {
        sessionStorage.setItem(`selected_release_submission_${currentArtistId}`, selectedRelease);
      }

      // If we have uploaded files, create submissions.
      // - Videos: one submission per video (existing behavior).
      // - Images: ONE carousel submission containing all image URLs
      //   (is_photo_post=true, image_urls=[...], video_url=first image
      //   as a fallback for backwards compatibility).
      if (completedFiles.length > 0) {
        const useCustomName = projectNameTouched && formData.projectName.trim().length > 0;

        const videoFiles = completedFiles.filter(f => f.kind === 'video');
        const imageFiles = completedFiles.filter(f => f.kind === 'image');

        // One submission per video
        for (const pf of videoFiles) {
          const fileBaseName = pf.originalFileName || pf.file.name.replace(/\.[^/.]+$/, '');
          const name = useCustomName
            ? (videoFiles.length > 1
              ? `${formData.projectName.trim()} - ${fileBaseName}`
              : formData.projectName.trim())
            : fileBaseName;

          await addSubmission({
            projectName: name,
            videoUrl: pf.url!,
            type: formData.type,
            artistId: currentArtistId,
            notes: formData.notes,
            status: 'new',
            release_id: selectedRelease || null
          } as any, artistClient || undefined).catch(error => {
            console.error('Error in submission form:', error);
            throw error;
          });
        }

        // One carousel submission for all images
        if (imageFiles.length > 0) {
          const imageUrls = imageFiles.map(f => f.url!).filter(Boolean);
          const firstImage = imageFiles[0];
          const fileBaseName = firstImage.originalFileName || firstImage.file.name.replace(/\.[^/.]+$/, '');
          const name = useCustomName
            ? formData.projectName.trim()
            : fileBaseName;

          await addSubmission({
            projectName: name,
            videoUrl: imageUrls[0]!, // first image as fallback
            type: formData.type,
            artistId: currentArtistId,
            notes: formData.notes,
            status: 'new',
            release_id: selectedRelease || null,
            isPhotoPost: true,
            imageUrls,
          } as any, artistClient || undefined).catch(error => {
            console.error('Error in submission form:', error);
            throw error;
          });
        }
      } else {
        // Single URL submission
        await addSubmission({
          ...formData,
          artistId: currentArtistId,
          status: 'new',
          release_id: selectedRelease || null
        } as any, artistClient || undefined).catch(error => {
          console.error('Error in submission form:', error);
          throw error;
        });
      }
      
      onClose();
    } catch (error) {
      console.error('Error submitting video:', error);
      if (error instanceof Error) {
        if (error.message === 'duplicate_entry') {
          setError('This video URL has already been submitted. Please check your submissions or use a different URL.');
        } else if (error.message === 'duplicate_submission_id') {
          setError('A submission with this ID already exists. Please try again.');
        } else {
          setError(error.message);
        }
      } else {
        setError('Failed to submit video');
      }
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div 
        className="bg-white dark:bg-[#1F2937] rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold dark:text-white">
            {completedFiles.filter(f => f.kind === 'image').length > 0 && completedFiles.filter(f => f.kind === 'video').length === 0
              ? 'Submit Photo Slide'
              : 'Submit New Video'}
          </h2>
          <motion.button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <X className="h-5 w-5" />
          </motion.button>
        </div>

        <AnimatePresence>
          {error && (
            <motion.div 
              className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0 }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!artistId && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative"
              ref={dropdownRef}
            >
              <label className="block text-sm font-medium mb-1 dark:text-white">
                Artist <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={selectedArtistName || artistSearch}
                  onChange={(e) => {
                    setArtistSearch(e.target.value);
                    setShowArtistDropdown(true);
                    if (!e.target.value) {
                      setFormData({ ...formData, artistId: '' });
                    }
                  }}
                  onFocus={() => setShowArtistDropdown(true)}
                  placeholder="Search artists..."
                  disabled={loading || isUploading}
                  className="w-full pl-10 pr-10 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                {(formData.artistId || artistSearch) && !loading && !isUploading && (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, artistId: '' });
                      setArtistSearch('');
                      setShowArtistDropdown(false);
                    }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {/* Dropdown */}
              {showArtistDropdown && filteredArtists.length > 0 && !loading && !isUploading && (
                <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                  {filteredArtists.map((artist) => (
                    <button
                      key={artist.id}
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, artistId: String(artist.id) });
                        setArtistSearch('');
                        setShowArtistDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-white text-sm ${
                        String(formData.artistId) === String(artist.id) ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' : ''
                      }`}
                    >
                      {artist.name}
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Release Selection - only show if artist has releases */}
          {releases.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <label className="block text-sm font-medium mb-1 dark:text-white">
                Release
              </label>
              <select
                value={selectedRelease}
                onChange={(e) => {
                  setSelectedRelease(e.target.value);
                  // Clear pending files and URL when switching to an ad_only release
                  const release = releases.find(r => r.id === e.target.value);
                  if (release?.service_package === 'ad_only') {
                    setPendingFiles([]);
                    setFormData(prev => ({ ...prev, videoUrl: '' }));
                  }
                }}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary-500 focus:ring-primary-500"
                disabled={loading || isUploading}
              >
                <option value="">No Release Assigned</option>
                {releases.map((release) => (
                  <option key={release.id} value={release.id}>
                    {release.name}{release.release_date ? ` (${new Date(release.release_date).toLocaleDateString('de-DE', { day: 'numeric', month: 'numeric', year: 'numeric' })})` : ''}
                  </option>
                ))}
              </select>
              {selectedRelease && releases.find(r => r.id === selectedRelease)?.service_package === 'ad_only' && (
                <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/50 rounded-md">
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    ⚠️ This release doesn't have a <strong>Content-Feedback Package</strong>. Videos won't be reviewed for feedback.
                  </p>
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    Would you like to add existing posts or video files as <strong>Ad Creatives</strong> instead?
                  </p>
                  <a
                    href={`/artist/${artistId || formData.artistId}/ad-creatives`}
                    className="inline-block mt-2 px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-medium rounded transition-colors"
                  >
                    Go to Ad Creatives →
                  </a>
                </div>
              )}
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <label className="block text-sm font-medium mb-1 dark:text-white">Type</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="type"
                  value="song-specific"
                  checked={formData.type === 'song-specific'}
                  onChange={() =>
                    setFormData({
                      ...formData,
                      type: 'song-specific',
                    })
                  }
                  className="mr-2"
                  disabled={loading || isUploading}
                />
                <span className="dark:text-white">Song Specific</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="type"
                  value="off-topic"
                  checked={formData.type === 'off-topic'}
                  onChange={() =>
                    setFormData({
                      ...formData,
                      type: 'off-topic',
                    })
                  }
                  className="mr-2"
                  disabled={loading || isUploading}
                />
                <span className="dark:text-white">Off Topic</span>
              </label>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4"
          >
            <label className="block text-sm font-medium mb-1 dark:text-white">
              Video / Carousel <span className="text-red-500">*</span>
              {completedFiles.length > 0 && (
                <span className="text-green-500 text-xs ml-2">{completedFiles.length} file{completedFiles.length > 1 ? 's' : ''} ready</span>
              )}
            </label>
            <div className="flex flex-col gap-2">
              {/* Drag & Drop Zone */}
              {isAdOnlyRelease ? (
                <div className="w-full px-4 py-3 rounded-md border-2 border-dashed border-gray-300 dark:border-gray-600 opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800">
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="h-8 w-8 text-gray-400" />
                    <div className="text-sm font-medium dark:text-white">Uploads disabled for Ad-Only releases</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Use the Ad Creatives section instead
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleFileSelect}
                  className={`w-full px-4 py-3 rounded-md border-2 border-dashed cursor-pointer transition-colors ${
                    isDragOver
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  } ${(isUploading || isConverting) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex flex-col items-center gap-2">
                    {isConverting ? (
                      <>
                        <Loader className="h-8 w-8 text-primary-500 animate-spin" />
                        <div className="text-sm font-medium dark:text-white">
                          Converting HEIC to JPEG...
                        </div>
                      </>
                    ) : (
                      <>
                        <Upload className={`h-8 w-8 ${isDragOver ? 'text-primary-500' : 'text-gray-400'}`} />
                        <div className="text-sm font-medium dark:text-white">
                          {isDragOver ? 'Drop files here' : 'Click to upload or drag & drop'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          MP4, WebM, MOV (videos) or JPG, PNG, WebP, GIF, HEIC (carousel images) — multiple files supported
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />

              {/* Pending Files — images show as thumbnail grid, videos as list */}
              {pendingFiles.length > 0 && (() => {
                const imageFiles = pendingFiles.filter(f => f.kind === 'image');
                const videoFiles = pendingFiles.filter(f => f.kind === 'video');
                return (
                  <>
                    {/* Image thumbnail grid (carousel order) */}
                    {imageFiles.length > 0 && (
                      <div className="mt-2">
                        {imageFiles.length >= 2 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                            Carousel order ({imageFiles.length} images)
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <AnimatePresence>
                            {imageFiles.map((pf, idx) => (
                              <motion.div
                                key={pf.id}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                className="relative group"
                              >
                                {/* Order badge */}
                                {imageFiles.length >= 2 && (
                                  <div className="absolute top-1 left-1 z-10 bg-black/70 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                    {idx + 1}
                                  </div>
                                )}
                                {/* Thumbnail */}
                                <div className="w-20 h-20 rounded-md overflow-hidden bg-gray-200 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 relative">
                                  {pf.previewUrl ? (
                                    <img
                                      src={pf.previewUrl}
                                      alt={pf.file.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <ImageIcon className="h-6 w-6 text-gray-400" />
                                    </div>
                                  )}
                                  {/* Upload overlay */}
                                  {pf.status === 'uploading' && (
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                      <Loader className="h-5 w-5 text-white animate-spin" />
                                    </div>
                                  )}
                                </div>
                                {/* Status badge */}
                                {pf.status === 'done' && (
                                  <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-0.5">
                                    <CheckCircle className="h-3 w-3 text-white" />
                                  </div>
                                )}
                                {pf.status === 'error' && (
                                  <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5">
                                    <AlertCircle className="h-3 w-3 text-white" />
                                  </div>
                                )}
                                {/* Remove button */}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removePendingFile(pf.id);
                                  }}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </motion.div>
                            ))}
                          </AnimatePresence>
                        </div>
                        {/* Error details for images (if any) */}
                        {imageFiles.some(f => f.status === 'error') && (
                          <div className="mt-1 text-xs text-red-500">
                            {imageFiles.filter(f => f.status === 'error').map(f => f.file.name).join(', ')} failed to upload
                          </div>
                        )}
                      </div>
                    )}

                    {/* Video files — keep existing list format */}
                    {videoFiles.length > 0 && (
                      <div className="space-y-2 mt-2">
                        <AnimatePresence>
                          {videoFiles.map((pf) => (
                            <motion.div
                              key={pf.id}
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="flex items-center gap-3 p-3 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                            >
                              <div className="flex-shrink-0">
                                {pf.status === 'done' ? (
                                  <CheckCircle className="h-5 w-5 text-green-500" />
                                ) : pf.status === 'error' ? (
                                  <AlertCircle className="h-5 w-5 text-red-500" />
                                ) : (
                                  <FileVideo className="h-5 w-5 text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-sm font-medium dark:text-white truncate">
                                    {pf.file.name}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                                    {pf.fileSize}
                                  </span>
                                </div>
                                {pf.status === 'uploading' && (
                                  <div className="mt-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-primary-500 rounded-full transition-all duration-200"
                                      style={{ width: `${pf.progress}%` }}
                                    />
                                  </div>
                                )}
                                {pf.status === 'error' && (
                                  <div className="mt-1 text-xs text-red-500">{pf.error}</div>
                                )}
                                {pf.status === 'done' && (
                                  <div className="mt-0.5 text-xs text-green-500">Uploaded ✓</div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removePendingFile(pf.id);
                                }}
                                className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Carousel hint — multiple images become one carousel post */}
              {completedFiles.filter(f => f.kind === 'image').length >= 2 && (
                <div className="mt-1 text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                  <ImageIcon className="h-3 w-3" />
                  {completedFiles.filter(f => f.kind === 'image').length} images will be grouped into one carousel post.
                </div>
              )}

              {/* Unified URL input — hidden, auto-detects Dropbox folder URLs */}
              {false && pendingFiles.length === 0 && !isAdOnlyRelease && (
                <div className="mt-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 text-center">
                    or paste a video URL / Dropbox folder link
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.videoUrl}
                      onChange={(e) => {
                        setFormData({ ...formData, videoUrl: e.target.value });
                        // Clear folder logs when URL changes
                        if (folderLogs.length > 0) setFolderLogs([]);
                      }}
                      placeholder="https://... (video URL or Dropbox folder)"
                      disabled={loading || isUploading || isProcessingFolder}
                      className="flex-1 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                    />
                    {isDropboxFolderUrl(formData.videoUrl) && (
                      <button
                        type="button"
                        onClick={processDropboxFolder}
                        disabled={isProcessingFolder || !formData.videoUrl.trim()}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 flex-shrink-0"
                      >
                        {isProcessingFolder ? (
                          <>
                            <Loader className="h-3.5 w-3.5 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <FolderDown className="h-3.5 w-3.5" />
                            Import folder
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Dropbox folder hint */}
                  {isDropboxFolderUrl(formData.videoUrl) && (
                    <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                      Dropbox folder detected — click "Import folder" to download all videos as submissions.
                    </p>
                  )}

                  {/* Streaming logs for folder processing */}
                  {folderLogs.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto rounded bg-gray-900 dark:bg-black p-2 text-xs font-mono text-gray-300">
                      {folderLogs.map((log, i) => (
                        <div
                          key={i}
                          className={`${
                            log.type === 'error' ? 'text-red-400' :
                            log.type === 'success' ? 'text-green-400' :
                            log.type === 'progress' ? 'text-blue-400' :
                            'text-gray-400'
                          }`}
                        >
                          {log.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <label className="block text-sm font-medium mb-1 dark:text-white">
              Video Name {completedFiles.length === 0 && <span className="text-red-500">*</span>}
              {completedFiles.length > 0 && !projectNameTouched && (
                <span className="text-gray-400 text-xs ml-1">(auto-filled from filename — edit to override)</span>
              )}
            </label>
            <input
              type="text"
              value={formData.projectName}
              onChange={(e) => {
                setProjectNameTouched(true);
                setFormData({ ...formData, projectName: e.target.value });
              }}
              className="w-full rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder={completedFiles.length > 0 ? "Auto-filled from filename" : "Enter video name"}
              disabled={loading || isUploading}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <label className="block text-sm font-medium mb-1 dark:text-white">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any notes or comments about this submission..."
              className="w-full rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white h-32 resize-none"
              disabled={loading || isUploading}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              These notes will be visible in the feedback conversation.
            </p>
          </motion.div>

          <motion.div 
            className="flex justify-end space-x-2 pt-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
              disabled={loading || isUploading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Cancel
            </motion.button>
            <motion.button 
              type="submit" 
              className="btn disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || isUploading || hasUploadingFiles || isProcessingFolder}
              whileHover={!loading && !isUploading && !isProcessingFolder ? { scale: 1.05 } : {}}
              whileTap={!loading && !isUploading && !isProcessingFolder ? { scale: 0.95 } : {}}
            >
              {loading || isUploading || hasUploadingFiles ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  {isUploading || hasUploadingFiles ? 'Uploading...' : 'Submitting...'}
                </>
              ) : isProcessingFolder ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Processing folder...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {completedFiles.filter(f => f.kind === 'image').length > 0 && completedFiles.filter(f => f.kind === 'video').length === 0
                    ? 'Submit Photo Slide'
                    : completedFiles.filter(f => f.kind === 'video').length > 1
                      ? `Submit ${completedFiles.filter(f => f.kind === 'video').length} Videos`
                      : 'Submit Video'}
                </>
              )}
            </motion.button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
};
